import { LegalDocument } from '@westhetics/legal/react'
import {
  ActionError,
  Button,
  Checkbox,
  SignaturePad,
  WaitPhrase,
  cn,
  formatDate,
} from '@westhetics/ui'
import { useId, useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { FieldNote, PublicScreen, publicField, publicTextAction } from '@/app/public-screen'
import { noteText, ReadingSkeleton, supportText } from '@/app/shell'
import { isApiError } from '@/lib/api'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { routes } from '@/routes'
import {
  saveFile,
  type Signed,
  type Signing,
  type SigningDocument,
  useDecline,
  useReadDocument,
  useReceipt,
  useSign,
  useSigning,
} from './api'

/**
 * A paciente lê e assina pelo link (docs/areas/documentos-e-assinaturas.md, "Assinatura no celular do
 * paciente"; RN-ASS-15 a RN-ASS-23; T38). Arquétipo: fluxo em etapas, no celular. Cinco telas: abriu o
 * link, lendo cada documento com o aceite, assinar com o dedo, pronto (fundo ink) e, a saída, não
 * concordar. Sem sessão: o token do link identifica o envio.
 */

const MESSAGES = {
  invalidTitle: 'Este link não é válido.',
  invalidText: 'Confira se copiou o endereço inteiro. Se precisar, peça à clínica o link de novo.',
  expiredTitle: 'Este link venceu.',
  closedTitle: 'Este envio não está mais aberto.',
  readToContinue: 'Marque que leu para continuar',
  signToContinue: 'Assine para continuar',
  reason: 'Conte em poucas palavras o que você não concorda.',
  declinedTitle: 'Não assinado.',
  declinedText: 'A clínica foi avisada e vai falar com você.',
} as const

type Step =
  { kind: 'intro' } | { kind: 'read'; index: number } | { kind: 'sign' } | { kind: 'decline' }

const DOCUMENTS_WORD = (n: number) => `${n} documento${n === 1 ? '' : 's'}`

function LinkProblem({ title, text }: { title: string; text: string }) {
  return (
    <PublicScreen
      eyebrow="Assinatura"
      title={title}
      footer="© 2026 Westhetics · A beleza deve ser segura."
    >
      <p className={cn(supportText, 'mt-3')}>{text}</p>
    </PublicScreen>
  )
}

/** Um bloco antes do texto integral: o resumo em linguagem clara, ou o que pode acontecer (em Plum: é risco). */
function PlainBlock({ title, text, risk }: { title: string; text: string; risk?: boolean }) {
  return (
    <section
      aria-label={title}
      className={cn('bg-surface mt-5 border-l-2 px-4 py-3', risk ? 'border-plum' : 'border-lilac')}
    >
      <div className="eyebrow">{title}</div>
      <p className="font-body text-ink mt-2 text-[14px] leading-[1.6] font-light">{text}</p>
    </section>
  )
}

function IntroScreen({
  signing,
  readCount,
  onStart,
}: {
  signing: Signing
  readCount: number
  onStart: () => void
}) {
  const total = signing.documents.length
  const guardian = signing.signer.role === 'guardian'
  const label =
    readCount === 0
      ? 'Começar'
      : readCount === total
        ? 'Ir para a assinatura'
        : 'Continuar de onde parei'
  return (
    <PublicScreen
      eyebrow="Assinatura"
      title={`Olá, ${signing.signer.firstName}.`}
      footer={`O link vale até ${formatDate(new Date(signing.linkExpiresAt))} · © 2026 Westhetics`}
    >
      <p className={cn(supportText, 'mt-3')}>
        {guardian
          ? `A ${signing.clinic.name} preparou ${DOCUMENTS_WORD(total)} do procedimento de ${signing.patient.name}. Você assina como responsável legal. Leia com calma — você assina no final, aqui mesmo.`
          : `A ${signing.clinic.name} preparou ${DOCUMENTS_WORD(total)} do seu procedimento. Leia com calma — você assina no final, aqui mesmo.`}
      </p>
      <ol className="mt-6 flex flex-col" aria-label="Documentos para ler">
        {signing.documents.map((doc, index) => (
          <li
            key={doc.id}
            className="border-hairline flex items-baseline gap-3 border-t py-3 first:border-t-0"
          >
            <span className="font-display text-label w-5 shrink-0 text-[13px] leading-none">
              {index + 1}
            </span>
            <span className="font-display text-ink min-w-0 flex-1 text-[14px] leading-[1.35]">
              {doc.title}
            </span>
            {doc.readAt ? (
              <span className="font-body text-label shrink-0 text-[11px] leading-none">lido</span>
            ) : null}
          </li>
        ))}
      </ol>
      <p className={cn(noteText, 'mt-4')}>Leva cerca de {signing.estimatedMinutes} minutos.</p>
      <Button size="lg" className="mt-7" onClick={onStart}>
        {label}
      </Button>
    </PublicScreen>
  )
}

function ReadScreen({
  signing,
  doc,
  index,
  scale,
  onScale,
  onRead,
  pending,
  error,
}: {
  signing: Signing
  doc: SigningDocument
  index: number
  scale: 'normal' | 'large'
  onScale: (scale: 'normal' | 'large') => void
  onRead: () => void
  pending: boolean
  error: unknown
}) {
  const id = useId()
  const [read, setRead] = useState(Boolean(doc.readAt))
  const total = signing.documents.length
  return (
    <PublicScreen
      eyebrow={`Documento ${index + 1} de ${total}`}
      title={doc.title}
      wide
      header={
        <button
          type="button"
          aria-pressed={scale === 'large'}
          aria-label="Texto maior"
          onClick={() => onScale(scale === 'large' ? 'normal' : 'large')}
          className="font-display text-ink border-hairline-strong relative flex h-10 min-w-10 items-center justify-center border px-3 text-[15px]"
        >
          A+
        </button>
      }
      footer={`${doc.type} · ${signing.clinic.name}`}
    >
      {doc.summary ? <PlainBlock title="Em resumo" text={doc.summary} /> : null}
      {doc.risks ? <PlainBlock title="O que pode acontecer" text={doc.risks} risk /> : null}
      <div className="mt-6">
        <LegalDocument
          markdown={doc.markdown}
          scale={scale}
          clinicName={signing.clinic.name}
          showHeader={false}
        />
      </div>
      <div className="border-hairline-strong mt-8 flex items-start gap-3 border-t pt-5">
        <Checkbox
          id={`${id}-read`}
          checked={read}
          onCheckedChange={(value) => setRead(value === true)}
          aria-describedby={`${id}-read-note`}
          className="mt-[2px]"
        />
        <label
          htmlFor={`${id}-read`}
          className="font-display text-ink cursor-pointer text-[15px] leading-[1.4]"
        >
          Li e entendi este documento
        </label>
      </div>
      <FieldNote
        id={`${id}-read-note`}
        hint={
          doc.readAt
            ? `Você já marcou este documento como lido em ${formatDate(new Date(doc.readAt))}.`
            : undefined
        }
      />
      {error ? (
        <ActionError
          className="mt-5"
          title="Não foi possível registrar a leitura"
          description={errorMessage(error)}
        />
      ) : null}
      <Button
        size="lg"
        className="mt-6"
        disabled={!read}
        loading={pending}
        loadingLabel="Registrando…"
        onClick={onRead}
      >
        {read
          ? index + 1 === total
            ? 'Ir para a assinatura'
            : 'Continuar'
          : MESSAGES.readToContinue}
      </Button>
    </PublicScreen>
  )
}

function SignScreen({
  signing,
  onSign,
  onDecline,
  pending,
  error,
}: {
  signing: Signing
  onSign: (png: string) => void
  onDecline: () => void
  pending: boolean
  error: unknown
}) {
  const [png, setPng] = useState<string | null>(null)
  const total = signing.documents.length
  const fields = fieldErrors(error)
  return (
    <PublicScreen
      eyebrow="Último passo"
      title="Sua assinatura"
      footer="© 2026 Westhetics · A beleza deve ser segura."
    >
      <p className={cn(supportText, 'mt-3')}>
        Assine com o dedo. Vale para {total === 1 ? 'o documento' : `os ${total} documentos`}.
      </p>
      <SignaturePad className="mt-6" label="Sua assinatura" onChange={setPng} disabled={pending} />
      {fields.drawnSignature ? (
        <p role="alert" className="font-body text-plum mt-2 text-[12px] leading-[1.5] font-light">
          {fields.drawnSignature}
        </p>
      ) : null}
      <p className="font-body text-ink mt-6 text-[13px] leading-[1.6] font-light">
        {signing.evidenceNotice}
      </p>
      {error && !fields.drawnSignature ? (
        <ActionError
          className="mt-5"
          title="Não foi possível assinar"
          description={errorMessage(error)}
        />
      ) : null}
      <WaitPhrase moment="signing" active={pending} align="left" className="mt-4" />
      <Button
        size="lg"
        className="mt-6"
        disabled={!png}
        loading={pending}
        loadingLabel="Assinando…"
        onClick={() => png && onSign(png)}
      >
        {png ? `Assinar ${DOCUMENTS_WORD(total)}` : MESSAGES.signToContinue}
      </Button>
      <button type="button" onClick={onDecline} className={cn(publicTextAction, 'mt-6')}>
        Não concordo com algum documento
      </button>
    </PublicScreen>
  )
}

function DeclineScreen({
  onBack,
  onDecline,
  pending,
  error,
}: {
  onBack: () => void
  onDecline: (reason: string) => void
  pending: boolean
  error: unknown
}) {
  const id = useId()
  const [reason, setReason] = useState('')
  const [touched, setTouched] = useState(false)
  const own = reason.trim().length < 10 ? MESSAGES.reason : null
  const shown = (touched ? own : null) ?? fieldErrors(error).reason ?? null
  function submit(event: FormEvent) {
    event.preventDefault()
    setTouched(true)
    if (own) return
    onDecline(reason.trim())
  }
  return (
    <PublicScreen eyebrow="Não concordar" title="O que você não concorda?" onSubmit={submit}>
      <p className={cn(supportText, 'mt-3')}>
        Conte em poucas palavras. A clínica recebe o aviso e fala com você; nada é assinado.
      </p>
      <label htmlFor={id} className="eyebrow mt-8">
        Motivo
      </label>
      <textarea
        id={id}
        rows={4}
        value={reason}
        aria-invalid={shown ? true : undefined}
        aria-describedby={`${id}-note`}
        onChange={(event) => setReason(event.target.value)}
        onBlur={() => setTouched(true)}
        className={cn(publicField, 'resize-none leading-[1.5]')}
      />
      <FieldNote id={`${id}-note`} error={shown} />
      {error && !fieldErrors(error).reason ? (
        <ActionError
          className="mt-5"
          title="Não foi possível avisar a clínica"
          description={errorMessage(error)}
        />
      ) : null}
      <Button
        type="submit"
        variant="outline"
        size="lg"
        className="mt-7"
        loading={pending}
        loadingLabel="Avisando a clínica…"
      >
        Não assinar
      </Button>
      <button type="button" onClick={onBack} className={cn(publicTextAction, 'mt-6')}>
        Voltar para assinar
      </button>
    </PublicScreen>
  )
}

function DoneScreen({
  protocolNumber,
  signedAtLocalTime,
  hash,
  copySentTo,
  count,
  onDownload,
  downloading,
}: {
  protocolNumber: string
  signedAtLocalTime: string
  hash: string
  copySentTo: string | null
  count: number
  onDownload: () => void
  downloading: boolean
}) {
  return (
    <PublicScreen
      eyebrow="Assinado"
      title="Assinado."
      tone="ink"
      footer="A beleza deve ser segura."
    >
      <p className="font-body text-pampas/85 mt-3 text-[13px] leading-[1.6] font-light">
        {copySentTo
          ? `Enviamos ${count === 1 ? 'o documento' : `os ${count} documentos`} para ${copySentTo}. A clínica já foi avisada.`
          : `Guarde o protocolo ${protocolNumber}: a clínica tem a sua cópia. A clínica já foi avisada.`}
      </p>
      <div className="bg-pampas text-ink mt-6 px-5 py-4">
        <div className="font-display text-[14px] leading-[1.4]">
          {signedAtLocalTime} · Protocolo {protocolNumber}
        </div>
        <div className="font-body text-secondary mt-1 text-[12px] leading-[1.5] font-light break-all">
          Hash {hash}
        </div>
        <Button
          size="lg"
          className="mt-4 w-full"
          loading={downloading}
          loadingLabel="Reunindo os PDFs…"
          onClick={onDownload}
        >
          Baixar meus documentos
        </Button>
      </div>
      {copySentTo ? (
        <p className="font-body text-pampas/70 mt-6 text-[12px] leading-[1.6] font-light">
          Com este e-mail você entra no{' '}
          <a href={routes.signIn} className="text-pampas border-pampas/50 border-b">
            portal do paciente
          </a>{' '}
          e acompanha seus atendimentos e documentos.
        </p>
      ) : null}
    </PublicScreen>
  )
}

export function SignPage() {
  const [params] = useSearchParams()
  const token = params.get('t')
  const query = useSigning(token)
  const read = useReadDocument(token)
  const sign = useSign(token)
  const decline = useDecline(token)
  const receipt = useReceipt(token)
  const [step, setStep] = useState<Step>({ kind: 'intro' })
  const [scale, setScale] = useState<'normal' | 'large'>('normal')
  const [readIds, setReadIds] = useState<Set<string>>(() => new Set())
  const [signed, setSigned] = useState<Signed | null>(null)
  const [declined, setDeclined] = useState(false)

  if (!token) return <LinkProblem title={MESSAGES.invalidTitle} text={MESSAGES.invalidText} />
  if (declined) return <LinkProblem title={MESSAGES.declinedTitle} text={MESSAGES.declinedText} />

  function download() {
    receipt.mutate(undefined, {
      onSuccess: (file) => saveFile(file, 'Meus documentos.zip'),
    })
  }

  if (signed) {
    return (
      <DoneScreen
        protocolNumber={signed.protocolNumber}
        signedAtLocalTime={signed.signedAtLocalTime}
        hash={signed.hash}
        copySentTo={signed.copySentTo}
        count={signed.documents.length}
        onDownload={download}
        downloading={receipt.isPending}
      />
    )
  }

  if (query.isPending) {
    return (
      <PublicScreen eyebrow="Assinatura" title="Abrindo seus documentos…">
        <div className="mt-8">
          <ReadingSkeleton label="Carregando os documentos" />
        </div>
      </PublicScreen>
    )
  }
  if (query.isError) {
    const error = query.error
    if (isApiError(error)) {
      if (error.code === 'link_invalid')
        return <LinkProblem title={MESSAGES.invalidTitle} text={MESSAGES.invalidText} />
      if (error.code === 'link_expired')
        return <LinkProblem title={MESSAGES.expiredTitle} text={error.message} />
      if (error.status === 409 || error.status === 403)
        return <LinkProblem title={MESSAGES.closedTitle} text={error.message} />
    }
    return (
      <PublicScreen eyebrow="Assinatura" title="Seus documentos">
        <LoadError
          className="mt-6"
          title="Não conseguimos abrir seus documentos"
          error={error}
          onRetry={() => void query.refetch()}
        />
      </PublicScreen>
    )
  }

  const signing = query.data
  if (signing.state === 'signed' && signing.signature) {
    return (
      <DoneScreen
        protocolNumber={signing.signature.protocolNumber}
        signedAtLocalTime={signing.signature.signedAtLocalTime}
        hash={signing.signature.hash}
        copySentTo={signing.signature.copySentTo}
        count={signing.documents.length}
        onDownload={download}
        downloading={receipt.isPending}
      />
    )
  }

  const documents = signing.documents.map((d) =>
    readIds.has(d.id) && !d.readAt ? { ...d, readAt: new Date().toISOString() } : d,
  )
  const readCount = documents.filter((d) => d.readAt).length
  const firstUnread = documents.findIndex((d) => !d.readAt)

  function start() {
    setStep(firstUnread === -1 ? { kind: 'sign' } : { kind: 'read', index: firstUnread })
  }

  if (step.kind === 'read') {
    const doc = documents[step.index]!
    return (
      <ReadScreen
        key={doc.id}
        signing={signing}
        doc={doc}
        index={step.index}
        scale={scale}
        onScale={setScale}
        pending={read.isPending}
        error={read.error}
        onRead={() =>
          read.mutate(
            { documentId: doc.id, hash: doc.hash, scale },
            {
              onSuccess: (result) => {
                setReadIds((ids) => new Set(ids).add(doc.id))
                const nextIndex = result.nextDocumentId
                  ? documents.findIndex((d) => d.id === result.nextDocumentId)
                  : -1
                setStep(
                  result.hasReadAll || nextIndex === -1
                    ? { kind: 'sign' }
                    : { kind: 'read', index: nextIndex },
                )
              },
            },
          )
        }
      />
    )
  }

  if (step.kind === 'sign') {
    return (
      <SignScreen
        signing={signing}
        pending={sign.isPending}
        error={sign.error}
        onDecline={() => setStep({ kind: 'decline' })}
        onSign={(png) =>
          sign.mutate(
            {
              drawnSignature: {
                format: 'png',
                content: png.replace(/^data:image\/png;base64,/, ''),
              },
              accepted: true,
              documentHashes: documents.map((d) => ({ documentId: d.id, hash: d.hash })),
              device: {
                platform: navigator.platform?.slice(0, 40),
                screenWidth: window.innerWidth,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone?.slice(0, 60),
                language: navigator.language?.slice(0, 10),
              },
            },
            { onSuccess: setSigned },
          )
        }
      />
    )
  }

  if (step.kind === 'decline') {
    return (
      <DeclineScreen
        pending={decline.isPending}
        error={decline.error}
        onBack={() => setStep({ kind: 'sign' })}
        onDecline={(reason) => decline.mutate(reason, { onSuccess: () => setDeclined(true) })}
      />
    )
  }

  return <IntroScreen signing={signing} readCount={readCount} onStart={start} />
}
